import React, { useState, useEffect, useRef } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader, Tooltip, Spinner } from 'reactstrap'
import Select from '../../../shared/Select'
import { ReactComponent as WarningIcon } from '../../../assets/icons/Warning copy.svg'
import { ReactComponent as ArrowNextIcon } from '../../../assets/icons/arrow_next.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg'
import './MapAllRemainingModal.scss'
import { getSuggestProductMap } from '../../../services/ProductServices'
import { toaster } from '../../../shared/PFToast'
import createDebug from 'debug'

const MapAllRemainingModal = ({
    product,
    isOpen,
    onClose,
    mappedVariant,
    unmappedVariants = [],
    onApply,
    productArtworks = {},
    productId,
    isLoading = false,
}) => {
    const [selectedVariants, setSelectedVariants] = useState({})
    const [activeTab, setActiveTab] = useState('all')
    const [variantOptions, setVariantOptions] = useState({})

    const [catalogAttributes, setCatalogAttributes] = useState([])
    const [loadingAttributes, setLoadingAttributes] = useState(false)
    const [catalogProductVariants, setCatalogProductVariants] = useState([])
    const [tooltipOpen, setTooltipOpen] = useState({})
    const [selectTooltipOpen, setSelectTooltipOpen] = useState({})

    const debug = createDebug("pf126:PLAT2-729")

    // Store suggested mappings from API (immutable - used to check if variant has suggestion)
    const apiSuggestedMappingsRef = useRef([])

    // Store user's selected mappings (dynamic - used for sending to API)
    const [userMappings, setUserMappings] = useState([])

    // Fake API call to fetch product attributes
    useEffect(() => {
        const fetchProductAttributes = async () => {
            if (isOpen && mappedVariant?.variant_catalog) {
                setLoadingAttributes(true)
                try {
                    // Prepare catalog_product_id for API GET param
                    const catalog_product_id = mappedVariant.variant_catalog.product._id || mappedVariant.variant_catalog.product

                    const { success, data, message } = await getSuggestProductMap(productId, {
                        catalog_product_id: catalog_product_id,
                    })
                    if (!success) throw new Error(message)

                    setCatalogAttributes(data.catalog_product_attributes)
                    setCatalogProductVariants(data.catalog_product_variants)

                    // Store API suggested mappings in ref (immutable - used to check if variant has suggestion)
                    apiSuggestedMappingsRef.current = data.suggest_mapping

                    // Initialize user mappings with API suggestions
                    setUserMappings(data.suggest_mapping)

                } catch (error) {
                    toaster({
                        type: 'error',
                        message: error?.message,
                    })
                } finally {
                    setLoadingAttributes(false)
                }
            }
        }

        fetchProductAttributes()
    }, [isOpen, mappedVariant])

    useEffect(() => {
        if (isOpen && unmappedVariants.length > 0 && catalogProductVariants.length > 0) {
            // Initialize variants: only check those with suggested mappings from API
            const initialSelected = {}
            unmappedVariants.forEach((variant) => {
                // Check if this variant has a suggested mapping from API (immutable check)
                const hasSuggestion = apiSuggestedMappingsRef.current.some(m => m.variant_id === variant._id && m.sku !== null)
                initialSelected[variant._id] = hasSuggestion
            })
            setSelectedVariants(initialSelected)

            // Initialize variant options dynamically based on catalog attributes and API suggested mappings
            const initialOptions = {}
            const attributes = catalogAttributes.length > 0 ? catalogAttributes : []

            unmappedVariants.forEach((variant) => {
                const variantAttrs = {}

                // Check if there's a suggested mapping for this variant from API
                const apiSuggestedMapping = apiSuggestedMappingsRef.current.find(m => m.variant_id === variant._id)

                if (apiSuggestedMapping && catalogProductVariants.length > 0) {
                    // Find the catalog variant by SKU
                    const catalogVariant = catalogProductVariants.find(cv => cv.sku === apiSuggestedMapping.sku)


                    if (catalogVariant && catalogVariant.options) {
                        // Pre-fill with suggested values
                        catalogVariant.options.forEach((attr) => {
                            debug('catalogVariant', attr)
                            variantAttrs[attr.attribute_type] = attr.value
                        })
                    }
                }

                // Ensure all attribute types are present (fill missing ones with null)
                attributes.forEach((attr) => {
                    if (!(attr.value_type in variantAttrs)) {
                        variantAttrs[attr.value_type] = null
                    }
                })
                debug('variantAttrs', variantAttrs)

                initialOptions[variant._id] = variantAttrs
            })
            debug('Initialized variant options:', initialOptions)
            setVariantOptions(initialOptions)
        }
    }, [isOpen, unmappedVariants, catalogAttributes, catalogProductVariants])

    // Sync userMappings with selectedVariants and variantOptions
    useEffect(() => {
        if (!isOpen || catalogProductVariants.length === 0) return

        const updatedMappings = []

        // Loop through all selected variants
        Object.keys(selectedVariants).forEach((variantId) => {
            if (selectedVariants[variantId]) {
                // Variant is checked - find matching catalog variant
                const selectedOptions = variantOptions[variantId]
                if (selectedOptions && Object.keys(selectedOptions).length > 0) {
                    // Find catalog variant that matches all selected attributes
                    const matchingVariant = catalogProductVariants.find(catalogVariant => {
                        // Check if all selected attributes match
                        return Object.keys(selectedOptions).every(attributeType => {
                            const selectedValue = selectedOptions[attributeType]
                            if (!selectedValue) return false // Skip null/empty values
                            const catalogAttribute = catalogVariant.options?.find(attr => attr.attribute_type === attributeType)
                            return catalogAttribute && catalogAttribute.value === selectedValue
                        })
                    })

                    if (matchingVariant) {
                        updatedMappings.push({
                            variant_id: variantId,
                            sku: matchingVariant.sku
                        })
                    }
                }
            }
        })

        // Update userMappings only if changed
        setUserMappings((prev) => {
            // Create copies before sorting to avoid mutating original arrays
            const prevCopy = [...prev].sort((a, b) => a.variant_id.localeCompare(b.variant_id))
            const newCopy = [...updatedMappings].sort((a, b) => a.variant_id.localeCompare(b.variant_id))

            const prevStr = JSON.stringify(prevCopy)
            const newStr = JSON.stringify(newCopy)

            const hasChanged = prevStr !== newStr

            // Only log when there's an actual change (user action)
            if (hasChanged) {
                debug('userMappings updated (ready for API call):', updatedMappings)
            }

            return hasChanged ? updatedMappings : prev
        })
    }, [selectedVariants, variantOptions, catalogProductVariants, isOpen])

    if (!mappedVariant) {
        return null
    }

    const catalogProduct = mappedVariant.variant_catalog
    const artworks = productArtworks[mappedVariant._id] || []
    debug('catalogProduct.product', catalogProduct.product)

    debug('product', product, mappedVariant, productArtworks)

    const handleToggleVariant = (variantId) => {
        const isCurrentlyChecked = selectedVariants[variantId]

        // Check if this variant has a suggested mapping from API (immutable check)
        const hasSuggestion = apiSuggestedMappingsRef.current.some(m => m.variant_id === variantId && m.sku !== null)

        // Toggle the checkbox
        setSelectedVariants((prev) => ({
            ...prev,
            [variantId]: !prev[variantId],
        }))

        // If unchecking AND variant doesn't have suggestion, reset all selectbox values for this variant
        if (isCurrentlyChecked && !hasSuggestion) {
            setVariantOptions((prev) => ({
                ...prev,
                [variantId]: {},
            }))
        }
    }

    const handleToggleAll = (checked) => {
        const newSelected = {}
        getFilteredVariants().forEach((variant) => {
            newSelected[variant._id] = checked
        })
        setSelectedVariants((prev) => ({
            ...prev,
            ...newSelected,
        }))
    }

    const handleOptionChange = (variantId, optionType, value) => {
        // Update variant options
        const updatedOptions = {
            ...variantOptions,
            [variantId]: {
                ...variantOptions[variantId],
                [optionType]: value,
            },
        }
        setVariantOptions(updatedOptions)

        // Check if this variant doesn't have a suggested mapping from API (immutable check)
        const hasSuggestion = apiSuggestedMappingsRef.current.some(m => m.variant_id === variantId && m.sku !== null)

        if (!hasSuggestion) {
            // Get active attributes
            const attributes = catalogAttributes.length > 0 ? catalogAttributes : catalogProduct?.attributes || []

            // Check if all attributes for this variant have values
            const allAttributesFilled = attributes.every(attr => {
                return updatedOptions[variantId]?.[attr.value_type]
            })

            // Auto-check the checkbox if all attributes are filled
            if (allAttributesFilled) {
                setSelectedVariants((prev) => ({
                    ...prev,
                    [variantId]: true,
                }))
            } else {
                // Uncheck if not all filled
                setSelectedVariants((prev) => ({
                    ...prev,
                    [variantId]: false,
                }))
            }
        }
    }

    const toggleTooltip = (variantId) => {
        setTooltipOpen((prev) => ({
            ...prev,
            [variantId]: !prev[variantId],
        }))
    }

    const toggleSelectTooltip = (key) => {
        setSelectTooltipOpen((prev) => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    const handleVariantsListScroll = () => {
        // Dismiss all tooltips on scroll to prevent them from floating above the scrollable area
        setTooltipOpen({})
        setSelectTooltipOpen({})
    }

    const handleApply = () => {
        const selectedIds = Object.keys(selectedVariants).filter((id) => selectedVariants[id])
        if (selectedIds.length > 0) {
            // Pass userMappings which contains the SKU mappings for each variant
            onApply(selectedIds, mappedVariant._id, userMappings)
        }
    }

    const handleSkip = () => {
        onClose()
    }

    const getFilteredVariants = () => {
        if (activeTab === 'selected') {
            return unmappedVariants.filter(variant => selectedVariants[variant._id])
        }
        if (activeTab === 'unselected') {
            return unmappedVariants.filter(variant => !selectedVariants[variant._id])
        }
        return unmappedVariants
    }

    const filteredVariants = getFilteredVariants()
    const selectedCount = Object.values(selectedVariants).filter(Boolean).length
    const totalCount = unmappedVariants.length
    debug('variantOptions', variantOptions, unmappedVariants)

    // Get attribute options from fetched catalog attributes
    const getAttributeOptions = (attributeType) => {
        // Use fetched catalogAttributes if available, fallback to catalogProduct.attributes
        const attributes = catalogAttributes.length > 0 ? catalogAttributes : catalogProduct?.attributes || []
        const attr = attributes.find((a) => a.value_type === attributeType)
        return attr?.values?.map((v) => ({ label: v.name, value: v.value, color: v.color_code })) || []
    }

    // Get active attributes to render dynamically
    const activeAttributes = catalogAttributes.length > 0 ? catalogAttributes : catalogProduct?.attributes || []

    const allSelected = filteredVariants.length > 0 && filteredVariants.every((v) => selectedVariants[v._id])

    const customCloseButton = (
        <button className="close" onClick={onClose} aria-label="Close">
            <CloseIcon />
        </button>
    )

    return (
        <Modal isOpen={isOpen} toggle={onClose} className="map-all-remaining-modal" size="xl" centered scrollable>
            <ModalHeader toggle={onClose} className="map-all-remaining-modal__header" close={customCloseButton}>
                Map all remaining variants in one click
            </ModalHeader>

            <ModalBody className="map-all-remaining-modal__body" onScroll={handleVariantsListScroll}>
                <p className="map-all-remaining-modal__description">
                    You've successfully mapped <strong>{mappedVariant.sku || mappedVariant._id}</strong>. Apply the
                    same product and artwork to the <strong>{unmappedVariants.length}</strong> unmapped variants below -
                    or adjust individually before confirming.
                </p>

                {/* Mapping Configuration Section */}
                <div className="map-all-remaining-modal__section">
                    <div className="map-all-remaining-modal__section-config">
                        <div className="map-all-remaining-modal__section-artwork-column">
                            <h4 className="map-all-remaining-modal__section-title__config">MAPPING CONFIGURATION</h4>
                            <div className="map-all-remaining-modal__mapping-config">

                                {/* Shopify Product */}
                                <div className="config-item">
                                    <div className="config-product">
                                        <div className="product-image">
                                            {mappedVariant.image ? (
                                                <img src={mappedVariant.image} alt={mappedVariant.sku} />
                                            ) : (
                                                <div className="product-image-placeholder" />
                                            )}
                                        </div>
                                        <div className="product-info">
                                            <div className="config-label">Shopify product</div>
                                            <div className="product-name" title={product.title || 'Variant'}>{product.title || 'Variant'}</div>
                                            <div className="product-meta">
                                                <span>SKU: {mappedVariant.sku_seller}</span>
                                                {mappedVariant.options?.map((opt, idx) => (
                                                    <span key={idx}>
                                                        {opt.attribute.name.charAt(0).toUpperCase() + opt.attribute.name.slice(1)}: {opt.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="config-arrow">
                                    <ArrowNextIcon />
                                </div>

                                {/* Merchize Product */}
                                <div className="config-item">
                                    <div className="config-product">
                                        <div className="product-image">
                                            {catalogProduct?.product?.thumbnail_link ? (
                                                <img src={catalogProduct.product.thumbnail_link} alt={catalogProduct.product.title} />
                                            ) : (
                                                <div className="product-image-placeholder" />
                                            )}
                                        </div>
                                        <div className="product-info">
                                            <div className="config-label merchize">Merchize product</div>
                                            <div className="product-name" title={catalogProduct?.product?.title}>{catalogProduct?.product?.title}</div>
                                            <div className="product-meta">
                                                <span>SKU: {catalogProduct?.product?.sku}</span>
                                                {catalogProduct?.option?.map((opt, idx) => (
                                                    <span key={idx}>
                                                        {opt.name.charAt(0).toUpperCase() + opt.name.slice(1)}: {opt.option}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="map-all-remaining-modal__section-artwork">
                            <h4 className="map-all-remaining-modal__section-title__config">SELLER ARTWORK</h4>
                            <div className="artwork-preview">
                                {productArtworks[mappedVariant._id] && productArtworks[mappedVariant._id].length > 0 ? (
                                    (() => {
                                        const firstArtwork = productArtworks[mappedVariant._id][0];
                                        const { artworks } = firstArtwork;
                                        const firstKey = Object.keys(artworks)[0];
                                        return (
                                            <div className="artwork-item">
                                                {artworks[firstKey] && (
                                                    <img src={artworks[firstKey].thumbnail} alt="Artwork" />
                                                )}
                                            </div>
                                        )
                                    })()
                                ) : (
                                    <div className="artwork-placeholder">No artwork</div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Apply to variants Section */}
                <div className="map-all-remaining-modal__section">
                    <div className="map-all-remaining-modal__section-header">
                        <h4 className="map-all-remaining-modal__section-title">
                            Apply to <span>{selectedCount}</span>/{totalCount} variant{totalCount !== 1 ? 's' : ''}
                        </h4>

                        {/* Tabs */}
                        <div className="map-all-remaining-modal__tabs">
                            <button
                                className={`tab ${activeTab === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveTab('all')}
                            >
                                All variants
                            </button>
                            <button
                                className={`tab ${activeTab === 'selected' ? 'active' : ''}`}
                                onClick={() => setActiveTab('selected')}
                            >
                                Selected
                            </button>
                            <button
                                className={`tab ${activeTab === 'unselected' ? 'active' : ''}`}
                                onClick={() => setActiveTab('unselected')}
                            >
                                Unselected
                            </button>
                        </div>
                    </div>

                    {/* Variants List */}
                    <div className="map-all-remaining-modal__variants-list">
                        {/* Column Headers */}
                        <div className="variants-list-header">
                            <div className="header-checkbox"></div>
                            <div className="header-info">Store variant</div>
                            <div className="header-mapping">Will map to</div>
                        </div>

                        {/* Variants Container */}
                        <div className="variants-list-body">
                            {loadingAttributes ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                                    <Spinner style={{ color: '#007BFF' }} />
                                </div>
                            ) : (
                                filteredVariants.map((variant) => {
                                    // Check if this variant has a suggested mapping from API (immutable check)
                                    const hasSuggestion = apiSuggestedMappingsRef.current.some(m => m.variant_id === variant._id && m.sku !== null)

                                    const tooltipId = `variant-tooltip-${variant._id}`

                                    return (
                                        <React.Fragment key={variant._id}>
                                            <div
                                                id={tooltipId}
                                                className={`variant-row ${!selectedVariants[variant._id] ? 'variant-row--unselected' : ''}`}
                                            >
                                                <div className="variant-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedVariants[variant._id] || false}
                                                        onChange={() => handleToggleVariant(variant._id)}
                                                        disabled={!hasSuggestion && !selectedVariants[variant._id]}
                                                    />
                                                </div>

                                                <div className="variant-info">
                                                    <div className="variant-details">
                                                        <div className="variant-name">{variant.title || variant.sku}</div>
                                                    </div>
                                                </div>

                                                <div className="variant-mapping">
                                                    <div className="config-arrow">
                                                        <ArrowNextIcon />
                                                    </div>
                                                    {activeAttributes.map((attr, idx) => {
                                                        const options = getAttributeOptions(attr.value_type)
                                                        const valueString = variantOptions[variant._id]?.[attr.value_type]
                                                        // Find the matching option object from the options array
                                                        const selectedOption = options.find(opt => opt.value === valueString) || null
                                                        const selectTooltipKey = `${variant._id}-${attr.value_type}`
                                                        const selectTooltipId = `select-tooltip-${variant._id}-${idx}`

                                                        return (
                                                            <div key={attr._id || idx} className="variant-select-wrapper" id={selectTooltipId}>
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition={'fixed'}
                                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                                    className="variant-select"
                                                                    placeholder={`Select ${attr.name}`}
                                                                    options={options}
                                                                    value={selectedOption}
                                                                    onChange={(val) => handleOptionChange(variant._id, attr.value_type, val?.value)}
                                                                    isSearchable={false}
                                                                    isDisabled={hasSuggestion && !selectedVariants[variant._id]}
                                                                />
                                                                {selectedOption && (
                                                                    <Tooltip
                                                                        placement="top"
                                                                        isOpen={selectTooltipOpen[selectTooltipKey] || false}
                                                                        target={selectTooltipId}
                                                                        toggle={() => toggleSelectTooltip(selectTooltipKey)}
                                                                    >
                                                                        {selectedOption.label}
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                            {!hasSuggestion && (
                                                <Tooltip
                                                    placement="top"
                                                    isOpen={tooltipOpen[variant._id] || false}
                                                    target={tooltipId}
                                                    toggle={() => toggleTooltip(variant._id)}
                                                    style={{ maxWidth: '300px', textAlign: 'left' }}
                                                >
                                                    No matching variant found. Select a variant to map manually.
                                                </Tooltip>
                                            )}
                                        </React.Fragment>
                                    )
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Warning Note */}
                <div className="map-all-remaining-modal__warning">
                    <div className="warning-icon">
                        <WarningIcon />
                    </div>
                    <span>
                        <strong>Note:</strong> This will copy the product selection and artwork settings. You can still edit individual
                        variants manually after applying.
                    </span>
                </div>
            </ModalBody>

            <ModalFooter className="map-all-remaining-modal__footer">
                <button className="btn-secondary" onClick={handleSkip} disabled={isLoading}>
                    Skip for now, I'll map manually
                </button>
                <button
                    className="btn-primary"
                    onClick={handleApply}
                    disabled={selectedCount === 0 || isLoading}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" style={{ marginRight: '8px' }} />
                            Apply to All Variants
                        </>
                    ) : (
                        'Apply to All Variants'
                    )}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default MapAllRemainingModal
